import React, { useState } from "react";
import TermsModal from "./TermsModal"; 
import { FaFacebookF, FaLinkedinIn, FaMediumM, FaEnvelope, FaPhoneAlt, FaBuilding } from "react-icons/fa";
import ReferralModal from "./ReferralModal";
import demoPdf from '../assets/images/asdflo_demo.pdf';
import LogoWithSlogan from "./LogoWithSlogan";

const termsContent = (
  <div className="space-y-5 text-left max-w-4xl mx-auto text-gray-700 dark:text-gray-200">
    <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Terms and Conditions</h3>
    <p className="leading-relaxed mb-6">Welcome to asdflo, operated by asdflo Innovations OPC Pvt Ltd. By using our platform, you agree to these terms and conditions.</p>
    
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">1. Platform Usage</h4>
        <p className="leading-relaxed">asdflo is a platform connecting parents, professionals, and centers in the neurodiversity community. We do not guarantee employment to any candidate using our platform.</p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">2. User Responsibilities</h4>
        <ul className="list-disc pl-6 space-y-2 leading-relaxed">
          <li>You are responsible for the accuracy of the information you provide.</li>
          <li>You must not share false or misleading information.</li>
          <li>You understand that asdflo is only a platform and does not guarantee job placements.</li>
        </ul>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">3. Intellectual Property</h4>
        <p className="leading-relaxed">All content on this platform is the property of asdflo Innovations OPC Pvt Ltd and protected by copyright laws.</p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">4. Data Protection and Unauthorized Use</h4>
        <p className="leading-relaxed mb-3">You agree not to copy, scrape, download, or otherwise extract any data or content from this website without our express written permission. Unauthorized use of any content, including but not limited to:</p>
        <ul className="list-disc pl-6 space-y-2 leading-relaxed mb-3">
          <li>Web scraping or data mining</li>
          <li>Automated data collection or extraction</li>
          <li>Unauthorized downloading or copying of user data</li>
          <li>Reverse engineering or decompiling the platform</li>
        </ul>
        <p className="leading-relaxed">is strictly prohibited and may be subject to legal action under applicable cyber crime laws, including but not limited to the Information Technology Act, 2000 and its amendments. Violations may result in civil and criminal penalties.</p>
      </div>
    </div>
    
    <p className="mt-8 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">Last updated: July 5, 2024</p>
  </div>
);

const privacyPolicyContent = (
  <div className="space-y-6 text-left max-w-4xl mx-auto text-gray-700 dark:text-gray-200">
    <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Privacy Policy</h3>
    <p className="leading-relaxed mb-6">This Privacy Policy explains how asdflo Innovations OPC Pvt Ltd ("we," "our," or "us") collects, uses, and protects your information.</p>
    
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">1. Information We Collect</h4>
        <ul className="list-disc pl-6 space-y-2 leading-relaxed">
          <li>Personal information (name, email, phone number)</li>
          <li>Professional details (for therapists and centers)</li>
          <li>Usage data and cookies</li>
          <li>Job application details (for professionals)</li>
        </ul>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">2. How We Use Your Information</h4>
        <ul className="list-disc pl-6 space-y-2 leading-relaxed">
          <li>To provide and improve our services</li>
          <li>To connect professionals with opportunities</li>
          <li>To communicate with you</li>
          <li>To comply with legal obligations</li>
        </ul>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">3. Data Security</h4>
        <p className="leading-relaxed">We implement appropriate security measures to protect your information. However, no method of transmission over the internet is 100% secure.</p>
      </div>
      
      <div>
        <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">4. Third-Party Services</h4>
        <p className="leading-relaxed">We may use third-party services that collect, monitor, and analyze information to improve our services.</p>
      </div>
    </div>
    
    <p className="mt-8 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">Last updated: July 5, 2024</p>
  </div>
);

export default function Footer() {
  const [referralModal, setReferralModal] = useState({ open: false, role: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");

  const openModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTitle("");
    setModalContent(null);
  };

  return (
    <footer className="w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-200 border-t border-gray-700 shadow-2xl relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/20 via-purple-900/20 to-blue-900/20 animate-gradient" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
            {/* Navigation Links - Left Side */}
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
              <a href="/about" className="text-gray-400 hover:text-white transition-colors">About</a>
              <a href="/services" className="text-gray-400 hover:text-white transition-colors">Services</a>
              <a href="https://medium.com/@asdfloteam" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Blog</a>
              <a href="/membership" className="text-gray-400 hover:text-white transition-colors">Pricing</a>
              <a href="mailto:hello@asdflo.com" className="text-gray-400 hover:text-white transition-colors">Contact</a>
              <button onClick={() => openModal('Terms & Conditions', termsContent)} className="text-gray-400 hover:text-white transition-colors">Terms</button>
              <button onClick={() => openModal('Privacy Policy', privacyPolicyContent)} className="text-gray-400 hover:text-white transition-colors">Privacy</button>
            </div>

            {/* Logo and Social - Right Side */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <img
                  src={require("../assets/asdflologoh.png")} 
                  alt="asdflo logo"
                  className="h-5 w-auto object-contain"
                  style={{ maxWidth: '20px' }}
                />
                <span 
                  className="text-lg font-bold text-white leading-none"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    letterSpacing: '-0.03em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  asdflo
                </span>
              </div>
              <div className="flex space-x-3">
                {[
                  { icon: FaFacebookF, url: "https://www.facebook.com/profile.php?id=61575316464563" },
                  { icon: FaLinkedinIn, url: "https://www.linkedin.com/in/asd-flo-558057360/" },
                  { icon: FaMediumM, url: "https://medium.com/@asdfloteam" }
                ].map((social, index) => (
                  <a 
                    key={index}
                    href={social.url}
                    aria-label={social.icon.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    <social.icon className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-3 pt-3 border-t border-gray-800/50">
          <div className="flex flex-col xs:flex-row justify-between items-center space-y-1 xs:space-y-0">
            <p className="text-[9px] text-gray-500">
              © {new Date().getFullYear()} asdflo Innovations OPC Pvt Ltd • CIN: U12345MH2025OPC123456 • 
              asdflo is a platform and does not guarantee job placements or employment opportunities.
            </p>
            <button 
              onClick={() => setReferralModal({ open: true, role: null })}
              className="text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              Refer a Friend
            </button>
          </div>
        </div>
      </div>
      {modalOpen && (
        <TermsModal open={modalOpen} onClose={closeModal} title={modalTitle}>
          {modalContent}
        </TermsModal>
      )}
      <ReferralModal open={referralModal.open} onClose={() => setReferralModal({ open: false, role: null })} defaultRole={referralModal.role} />
    </footer>
  );
}
